import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authAPI } from '../utils/api';
import { useAuthStore } from '../store/authStore';
import '../styles/Profile.css';

const emptyProfile = {
  name: '',
  email: '',
  phone: '',
  address: { street: '', unit: '', city: '', province: '', postalCode: '' }
};

const Profile = () => {
  const { t } = useTranslation();
  const setUser = useAuthStore((state) => state.setUser);
  const [profile, setProfile] = useState(emptyProfile);
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await authAPI.getProfile();
        setProfile({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: {
            street: data.address?.street || '',
            unit: data.address?.unit || '',
            city: data.address?.city || '',
            province: data.address?.province || '',
            postalCode: data.address?.postalCode || ''
          }
        });
      } catch (error) {
        toast.error(t('profile.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [t]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfile((current) => ({ ...current, [name]: value }));
  };

  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    setProfile((current) => ({
      ...current,
      address: { ...current.address, [name]: value }
    }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const { data } = await authAPI.updateProfile(profile);
      const updatedUser = {
        id: data.user._id,
        name: data.user.name,
        email: data.user.email,
        role: data.user.role
      };
      setUser(updatedUser);
      toast.success(t('profile.saved'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    try {
      setSaving(true);
      await authAPI.updatePassword(passwords.currentPassword, passwords.newPassword);
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success(t('profile.passwordSaved'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('profile.passwordError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="profile-page">
      <h1>{t('profile.title')}</h1>
      <div className="profile-grid">
        <form className="profile-card" onSubmit={saveProfile}>
          <h2>{t('profile.personalInformation')}</h2>
          <label>{t('auth.name')}<input name="name" value={profile.name} onChange={handleProfileChange} required /></label>
          <label>{t('auth.email')}<input name="email" type="email" value={profile.email} onChange={handleProfileChange} required /></label>
          <label>{t('profile.phone')}<input name="phone" type="tel" value={profile.phone} onChange={handleProfileChange} required /></label>
          <h2>{t('booking.cleaningAddress')}</h2>
          <label>{t('booking.street')}<input name="street" value={profile.address.street} onChange={handleAddressChange} required /></label>
          <label>{t('booking.unitRequired')}<input name="unit" value={profile.address.unit} onChange={handleAddressChange} required /></label>
          <div className="profile-address-row">
            <label>{t('booking.city')}<input name="city" value={profile.address.city} onChange={handleAddressChange} required /></label>
            <label>{t('booking.province')}<input name="province" value={profile.address.province} onChange={handleAddressChange} required /></label>
            <label>{t('booking.postalCode')}<input name="postalCode" value={profile.address.postalCode} onChange={handleAddressChange} required /></label>
          </div>
          <button type="submit" disabled={saving}>{t('profile.saveProfile')}</button>
        </form>

        <form className="profile-card password-card" onSubmit={savePassword}>
          <h2>{t('profile.changePassword')}</h2>
          <label>{t('profile.currentPassword')}<input type="password" value={passwords.currentPassword} onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })} required /></label>
          <label>{t('profile.newPassword')}<input type="password" minLength="8" value={passwords.newPassword} onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })} required /></label>
          <label>{t('profile.confirmPassword')}<input type="password" minLength="8" value={passwords.confirmPassword} onChange={(event) => setPasswords({ ...passwords, confirmPassword: event.target.value })} required /></label>
          <button type="submit" disabled={saving}>{t('profile.updatePassword')}</button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
